import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Play, Pause, Send, X } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

interface EDMCampaign {
  _id: string;
  name: string;
  templateId: { _id: string; name: string; category: string };
  segmentId?: { _id: string; name: string };
  type: 'scheduled' | 'triggered' | 'manual';
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'cancelled';
  scheduleConfig?: any;
  triggerConfig?: any;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  lastSentAt?: string;
  nextSendAt?: string;
}

interface EDMTemplate {
  _id: string;
  name: string;
  category: string;
}

interface EDMSegment {
  _id: string;
  name: string;
}

export const CampaignManager: React.FC<{ onUpdate?: () => void }> = ({ onUpdate }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [campaigns, setCampaigns] = useState<EDMCampaign[]>([]);
  const [templates, setTemplates] = useState<EDMTemplate[]>([]);
  const [segments, setSegments] = useState<EDMSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<EDMCampaign | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [formData, setFormData] = useState({
    name: '',
    templateId: '',
    segmentId: '',
    type: 'manual' as 'scheduled' | 'triggered' | 'manual',
    scheduleConfig: {
      frequency: 'once' as 'once' | 'daily' | 'weekly' | 'monthly',
      startDate: '',
      endDate: '',
      timeOfDay: '09:00',
      dayOfWeek: [] as number[],
      dayOfMonth: 1
    },
    triggerConfig: {
      event: 'user_signup' as 'user_signup' | 'feature_not_used' | 'usage_milestone' | 'underutilization_detected',
      conditions: {}
    }
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Wait for auth to finish loading, then only fetch if authenticated
    if (authLoading) {
      return; // Still checking auth
    }
    
    if (isAuthenticated && user) {
      fetchCampaigns();
      fetchTemplates();
      fetchSegments();
    } else {
      // Not authenticated - set empty state
      setCampaigns([]);
      setTemplates([]);
      setSegments([]);
      setLoading(false);
    }
  }, [isAuthenticated, user, authLoading]);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated || !user) {
        setCampaigns([]);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setCampaigns([]);
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        setCampaigns([]);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated || !user) {
        setTemplates([]);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/templates?active=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setTemplates([]);
        return;
      }
      
      if (!response.ok) {
        setTemplates([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchSegments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated || !user) {
        setSegments([]);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/segments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setSegments([]);
        return;
      }
      
      if (!response.ok) {
        setSegments([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setSegments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
      setSegments([]);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingCampaign
        ? `${apiUrl}/api/edm/campaigns/${editingCampaign._id}`
        : `${apiUrl}/api/edm/campaigns`;

      const payload: any = {
        name: formData.name,
        templateId: formData.templateId,
        type: formData.type
      };

      if (formData.segmentId) {
        payload.segmentId = formData.segmentId;
      }

      if (formData.type === 'scheduled') {
        payload.scheduleConfig = {
          ...formData.scheduleConfig,
          startDate: formData.scheduleConfig.startDate ? new Date(formData.scheduleConfig.startDate) : new Date(),
          endDate: formData.scheduleConfig.endDate ? new Date(formData.scheduleConfig.endDate) : undefined
        };
      }

      if (formData.type === 'triggered') {
        payload.triggerConfig = formData.triggerConfig;
      }

      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        await fetchCampaigns();
        setEditingCampaign(null);
        resetForm();
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign');
    }
  };

  const handleSend = async (campaignId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        alert(`Campaign sent: ${data.data.sent} emails sent, ${data.data.failed} failed`);
        await fetchCampaigns();
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('Failed to send campaign');
    }
  };

  const handleDelete = async (campaignId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/edm/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        await fetchCampaigns();
        setShowDeleteConfirm(null);
        onUpdate?.();
      } else {
        alert(data.message || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      templateId: '',
      segmentId: '',
      type: 'manual',
      scheduleConfig: {
        frequency: 'once',
        startDate: '',
        endDate: '',
        timeOfDay: '09:00',
        dayOfWeek: [],
        dayOfMonth: 1
      },
      triggerConfig: {
        event: 'user_signup',
        conditions: {}
      }
    });
  };

  const handleEdit = (campaign: EDMCampaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      templateId: (campaign.templateId as any)._id || campaign.templateId,
      segmentId: campaign.segmentId ? ((campaign.segmentId as any)._id || campaign.segmentId) : '',
      type: campaign.type,
      scheduleConfig: campaign.scheduleConfig || {
        frequency: 'once',
        startDate: '',
        endDate: '',
        timeOfDay: '09:00',
        dayOfWeek: [],
        dayOfMonth: 1
      },
      triggerConfig: campaign.triggerConfig || {
        event: 'user_signup',
        conditions: {}
      }
    });
  };

  const filteredCampaigns = filterStatus === 'all'
    ? campaigns
    : campaigns.filter(c => c.status === filterStatus);

  if (loading) {
    return <div className="text-center py-8">{t('common.loading') || 'Loading...'}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('edm.campaigns.title') || 'Campaigns'}
        </h2>
        {!editingCampaign && (
          <button
            onClick={() => {
              resetForm();
              setEditingCampaign({} as EDMCampaign);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
          >
            <Plus className="w-5 h-5" />
            <span>{t('edm.campaigns.createCampaign') || 'Create Campaign'}</span>
          </button>
        )}
      </div>

      {/* Campaign Form */}
      {editingCampaign && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingCampaign._id ? t('edm.campaigns.editCampaign') || 'Edit Campaign' : t('edm.campaigns.createCampaign') || 'Create Campaign'}
            </h3>
            <button
              onClick={() => {
                setEditingCampaign(null);
                resetForm();
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.campaigns.name') || 'Campaign Name'}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.campaigns.template') || 'Template'}
              </label>
              <select
                value={formData.templateId}
                onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('edm.campaigns.selectTemplate') || 'Select Template'}</option>
                {templates.map(t => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.campaigns.segment') || 'Segment (Optional)'}
              </label>
              <select
                value={formData.segmentId}
                onChange={(e) => setFormData({ ...formData, segmentId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('edm.campaigns.allUsers') || 'All Users'}</option>
                {segments.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('edm.campaigns.type') || 'Type'}
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="manual">{t('edm.campaigns.typeManual') || 'Manual'}</option>
                <option value="scheduled">{t('edm.campaigns.typeScheduled') || 'Scheduled'}</option>
                <option value="triggered">{t('edm.campaigns.typeTriggered') || 'Triggered'}</option>
              </select>
            </div>

            {formData.type === 'scheduled' && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {t('edm.campaigns.scheduleConfig') || 'Schedule Configuration'}
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.campaigns.frequency') || 'Frequency'}
                  </label>
                  <select
                    value={formData.scheduleConfig.frequency}
                    onChange={(e) => setFormData({
                      ...formData,
                      scheduleConfig: { ...formData.scheduleConfig, frequency: e.target.value as any }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="once">{t('edm.campaigns.frequencyOnce') || 'Once'}</option>
                    <option value="daily">{t('edm.campaigns.frequencyDaily') || 'Daily'}</option>
                    <option value="weekly">{t('edm.campaigns.frequencyWeekly') || 'Weekly'}</option>
                    <option value="monthly">{t('edm.campaigns.frequencyMonthly') || 'Monthly'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.campaigns.startDate') || 'Start Date'}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduleConfig.startDate}
                    onChange={(e) => setFormData({
                      ...formData,
                      scheduleConfig: { ...formData.scheduleConfig, startDate: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('edm.campaigns.timeOfDay') || 'Time of Day'}
                  </label>
                  <input
                    type="time"
                    value={formData.scheduleConfig.timeOfDay}
                    onChange={(e) => setFormData({
                      ...formData,
                      scheduleConfig: { ...formData.scheduleConfig, timeOfDay: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700"
              >
                <span>{t('common.save') || 'Save'}</span>
              </button>
              <button
                onClick={() => {
                  setEditingCampaign(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {!editingCampaign && (
        <>
          {/* Filter */}
          <div className="flex items-center space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('edm.campaigns.filterAll') || 'All Status'}</option>
              <option value="draft">{t('edm.campaigns.status.draft') || 'Draft'}</option>
              <option value="scheduled">{t('edm.campaigns.status.scheduled') || 'Scheduled'}</option>
              <option value="active">{t('edm.campaigns.status.active') || 'Active'}</option>
              <option value="paused">{t('edm.campaigns.status.paused') || 'Paused'}</option>
              <option value="completed">{t('edm.campaigns.status.completed') || 'Completed'}</option>
            </select>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6">
              {filteredCampaigns.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('edm.campaigns.noCampaigns') || 'No campaigns found'}
                </p>
              ) : (
                <div className="space-y-3">
                  {filteredCampaigns.map(campaign => (
                    <div
                      key={campaign._id}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-medium text-gray-900 dark:text-white">{campaign.name}</h3>
                          <span className="px-2 py-1 text-xs rounded bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200">
                            {campaign.type}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs rounded ${
                              campaign.status === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : campaign.status === 'scheduled'
                                ? 'bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {t(`edm.campaigns.status.${campaign.status}`) || campaign.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {(campaign.templateId as any).name || 'Template'} • 
                          {' '}{campaign.totalSent || 0} {t('edm.campaigns.sent') || 'sent'} • 
                          {' '}{campaign.totalOpened || 0} {t('edm.campaigns.opened') || 'opened'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {campaign.type === 'manual' && (
                          <button
                            onClick={() => handleSend(campaign._id)}
                            className="p-2 text-success-500 hover:text-success-700"
                            title={t('edm.campaigns.sendNow') || 'Send Now'}
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(campaign)}
                          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          title={t('common.edit') || 'Edit'}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(campaign._id)}
                          className="p-2 text-error-500 hover:text-error-700"
                          title={t('common.delete') || 'Delete'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('edm.campaigns.deleteConfirm') || 'Delete Campaign?'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('common.confirmDelete') || 'Are you sure you want to delete this campaign? This action cannot be undone.'}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700"
              >
                {t('common.delete') || 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
